import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Loader2, ShieldCheck, Gauge, Users2, ArrowLeft } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { useAuth } from "../context/AuthContext";
import { Logo } from "../components/Logo";
import SEO from "../components/SEO";
import { toast } from "sonner";

export default function Login() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      navigate("/app");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen bg-background">
      <SEO title="Sign In — hireqix" description="Sign in to your hireqix workspace to screen and rank candidate resumes." canonicalPath="/login" />
      <div className="relative hidden w-1/2 flex-col justify-between overflow-hidden bg-[#0B1220] p-12 text-white lg:flex">
        <div
          className="absolute inset-0 opacity-[0.05]"
          style={{ backgroundImage: "radial-gradient(circle at 1px 1px, white 1px, transparent 0)", backgroundSize: "26px 26px" }}
        />
        <div className="relative flex items-center justify-between gap-4 animate-in fade-in-0 slide-in-from-left-4 duration-500">
          <Link to="/" className="rounded-xl bg-white px-4 py-3 shadow-lg" data-testid="login-logo-home-link">
            <Logo className="h-11" />
          </Link>
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 rounded-full border border-white/15 px-3.5 py-1.5 text-xs font-medium text-slate-300 transition-colors duration-150 hover:border-white/30 hover:text-white"
            data-testid="login-home-link"
          >
            <ArrowLeft className="h-3.5 w-3.5" /> Back to home
          </Link>
        </div>
        <div className="relative animate-in fade-in-0 slide-in-from-left-4 duration-700">
          <h2 className="font-heading text-3xl font-bold leading-tight tracking-tight">
            Evidence-based hiring decisions, at scale.
          </h2>
          <p className="mt-4 max-w-md text-sm leading-relaxed text-slate-400">
            Score every resume against your own competency matrix — consistent, auditable, and bias-resistant.
          </p>
          <div className="mt-10 space-y-4">
            <Feature icon={ShieldCheck} text="Role-based workspaces with full audit trail" />
            <Feature icon={Gauge} text="Weighted scoring that mirrors your hiring bar" />
            <Feature icon={Users2} text="Invite your whole hiring team in seconds" />
          </div>
        </div>
        <div className="relative text-xs text-slate-500">© {new Date().getFullYear()} hireqix</div>
      </div>

      <div className="flex w-full items-center justify-center px-6 lg:w-1/2">
        <div className="w-full max-w-sm animate-in fade-in-0 slide-in-from-bottom-2 duration-500">
          <div className="mb-10 flex items-center justify-between lg:hidden">
            <Link to="/" data-testid="login-home-link-mobile"><Logo className="h-12" /></Link>
            <Link to="/" className="inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground hover:text-foreground">
              <ArrowLeft className="h-3.5 w-3.5" /> Home
            </Link>
          </div>
          <h1 className="font-heading text-2xl font-bold tracking-tight">Welcome back</h1>
          <p className="mt-1 text-sm text-muted-foreground">Sign in to your workspace</p>
          <form onSubmit={submit} className="mt-8 space-y-4">
            <div>
              <label className="mb-1.5 block text-xs font-medium uppercase tracking-wide text-muted-foreground">Email</label>
              <Input
                type="email"
                required
                placeholder="you@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email-input"
              />
            </div>
            <div>
              <div className="mb-1.5 flex items-center justify-between">
                <label className="block text-xs font-medium uppercase tracking-wide text-muted-foreground">Password</label>
                <Link to="/forgot-password" className="text-xs font-medium text-primary hover:underline" data-testid="forgot-password-link">
                  Forgot password?
                </Link>
              </div>
              <Input
                type="password"
                required
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password-input"
              />
            </div>
            <Button type="submit" disabled={loading} className="w-full" size="lg" data-testid="login-submit-btn">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign In"}
            </Button>
          </form>
          <p className="mt-6 text-center text-sm text-muted-foreground">
            Don't have an account?{" "}
            <Link to="/signup" className="font-medium text-primary hover:underline" data-testid="go-to-signup-link">
              Sign up
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}

function Feature({ icon: Icon, text }) {
  return (
    <div className="flex items-center gap-3 text-sm text-slate-300">
      <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-white/5">
        <Icon className="h-4 w-4 text-sky-400" />
      </div>
      {text}
    </div>
  );
}
